import axios, { AxiosError } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { getServicesUrl, getSetupData, setup } from '../../setup';
import {
    APIVersion,
    PayloadRequestParameters,
    ServiceTypes,
    URLWithPayloadRequestParameters,
} from '../../types/main';
import Service from './service';
import {
    AuthenticatePayload,
    AuthenticateResponseData,
    PreAuthPayload,
    PreAuthResponseData,
} from '../../types/services/security';
import { getIdentityServerConfig } from '../../../configs/identity-config';
import { getNotificationHubConfig } from '../../../configs/notification-hub-config';
import {
    STATUS_OK, TokenScopes, TokenGrantTypes, TokenTypes,
} from '../../../constants/auth-constants';

export interface AppTokenRequestConfig {
    authority?: string,
    scope?: string,
    grant_type?: string,
    client_id?: string,
    tenant_uid?: string,
    response_type?: string,
    acr_values?: string,
    expires_in?: number,
    access_token?: string,
}

export interface RequestedTokenType {
    tokenType: string,
    tokenValue: string,
}

/**
 * Resolves the signed-in user's access token. Registered by the app's AuthProvider so this layer can
 * refresh the *user* token without importing the OIDC client (which would create a cycle).
 * Returns null when there is no user session, in which case the client_credentials fallback applies.
 */
export type MemberTokenResolver = () => Promise<string | null>;

let memberTokenResolver: MemberTokenResolver | null = null;

export const setMemberTokenResolver = (resolver: MemberTokenResolver | null) => {
    memberTokenResolver = resolver;
};

class Security extends Service {
    static showLogs: boolean = false;

    static showTokenLogs: boolean = false;

    static showSetTokensLogs: boolean = false;

    static async preAuth({
        payload,
        config,
    }: PayloadRequestParameters<PreAuthPayload>) {
        await Security.generateApplicationTokensIfNeeded();

        return Service.postRequest<PreAuthPayload, PreAuthResponseData>(
            `${getServicesUrl(APIVersion.V11)}/authentication/pre-authenticate`,
            payload,
            config,
        );
    }

    static isTokenExpired = (token) => {
        if (!token) { return true; }

        try {
            // `application_token` and `application_generic_token` are cached *with* the "Bearer "
            // prefix. Without stripping it, jwtDecode throws, this returns true for every check, and
            // both tokens get re-minted on every heartbeat tick.
            const decodedToken = jwtDecode(`${token}`.replace(/^Bearer\s+/i, ''));
            const currentTime = Date.now() / 1000;

            return decodedToken.exp !== undefined && decodedToken.exp < currentTime + 30;
        } catch (error) {
            if (Security.showLogs) {
                console.error('Error decoding token:', error);
            }

            return true;
        }
    };

    static async authenticate({
        urlParams,
        payload,
        config,
    }: URLWithPayloadRequestParameters<{ contactId: number }, AuthenticatePayload>) {
        await Security.generateApplicationTokensIfNeeded();

        return Service.postRequest<AuthenticatePayload, AuthenticateResponseData>(
            `${getServicesUrl(APIVersion.V11)}/contacts/${urlParams.contactId}/authentication/authenticate`,
            payload,
            config,
        );
    }

    static CacheTokenByType(tokenType: string, tokenValue: string) {
        sessionStorage.setItem(tokenType, tokenValue);
    }

    static GetCachedTokenByType(tokenType: string) {
        return sessionStorage.getItem(tokenType);
    }

    static getAppToken = (config: AppTokenRequestConfig): Promise<{ data: { access_token: string, expires_in?: number, apiError: any }, status: number }> => (
        axios.post(
            `${config.authority}/connect/token`,
            new URLSearchParams(Object.entries(config)).toString(),
            {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            },
        )
    );

    static generateNotificationHubAccessToken(jwt: string) {
        const getCachedToken = Security.GetCachedTokenByType(TokenTypes.notification_hub_token);

        if (getCachedToken && !Security.isTokenExpired(getCachedToken)) {
            setup({
                notification_hub_token: getCachedToken,
            });

            return new Promise((resolve, reject) => { resolve(getCachedToken); });
        }

        const notificationHubConfig = getNotificationHubConfig();
        const identityServerConfig = getIdentityServerConfig();

        const notificationHubTokenRequest: AppTokenRequestConfig = {
            authority: identityServerConfig.authority,
            scope: TokenScopes.NOTIFICATION_HUB_API,
            grant_type: TokenGrantTypes.GENERIC,
            client_id: identityServerConfig.client_id,
            tenant_uid: identityServerConfig.tenant_uid,
            response_type: identityServerConfig.response_type,
            acr_values: identityServerConfig.acr_values,
            access_token: jwt,
            expires_in: notificationHubConfig.expires_in,
        };

        if (Security.showLogs) { console.log('Notification Hub Access Token token is not set. Generating a new one...'); }

        return new Promise((resolve, reject) => {
            Security.getAppToken(notificationHubTokenRequest).then((response) => {
                if (response.status === STATUS_OK) {
                    setup({
                        notification_hub_token: `${response.data.access_token}`,
                    });
                } else {
                    reject(response.data.apiError);
                }

                if (Security.showTokenLogs) { console.log(`generateNotificationHubAccessToken(${jwt}) -> `, `${response.data.access_token}`); }

                Security.CacheTokenByType(TokenTypes.notification_hub_token, `${response.data.access_token}`);
                resolve(`${response.data.access_token}`);
            }).catch((e: AxiosError) => {
                reject(e);
            });
        });
    }

    static async addMemberToken() {
        const getCachedToken = Security.GetCachedTokenByType(TokenTypes.member_token);

        if (getCachedToken && !Security.isTokenExpired(getCachedToken)) {
            setup({
                member_token: getCachedToken,
            });

            return getCachedToken;
        }

        // A signed-in user's member token must come from the OIDC session. Minting a
        // client_credentials token here would produce a valid token with no member/contact subject,
        // which makes user-scoped endpoints answer 404.
        if (memberTokenResolver) {
            try {
                const userToken = await memberTokenResolver();

                if (userToken) {
                    setup({ member_token: userToken });
                    Security.CacheTokenByType(TokenTypes.member_token, userToken);

                    return userToken;
                }
            } catch (error) {
                if (Security.showLogs) { console.error('Failed to resolve the user member token:', error); }
            }
        }

        if (Security.showLogs) { console.log('Member token is not set. Generating a new one...'); }

        const identityServerConfig = getIdentityServerConfig();

        const memberTokenRequest: AppTokenRequestConfig = {
            authority: identityServerConfig.authority,
            scope: TokenScopes.DFS_API,
            grant_type: TokenGrantTypes.CLIENT_CREDENTIALS,
            client_id: identityServerConfig.client_id,
            tenant_uid: identityServerConfig.tenant_uid,
            response_type: identityServerConfig.response_type,
            acr_values: identityServerConfig.acr_values,
        };

        return new Promise((resolve, reject) => {
            Security.getAppToken(memberTokenRequest).then((response) => {
                if (response.status === STATUS_OK) {
                    setup({
                        member_token: `${response.data.access_token}`,
                    });
                } else {
                    reject(response.data.apiError);
                }

                if (Security.showTokenLogs) { console.log('addMemberToken() -> ', `${response.data.access_token}`); }

                Security.CacheTokenByType(TokenTypes.member_token, `${response.data.access_token}`);
                resolve(response.data);
            }).catch((e: AxiosError) => {
                reject(e);
            });
        });
    }

    static addGenericToken() {
        const setupData = getSetupData();
        const identityServerConfig = getIdentityServerConfig();

        const getCachedToken = Security.GetCachedTokenByType(TokenTypes.application_generic_token);

        if (getCachedToken && !Security.isTokenExpired(getCachedToken)) {
            setup({
                application_generic_token: getCachedToken,
            });

            return new Promise((resolve, reject) => { resolve(getCachedToken); });
        }

        if (Security.showLogs) { console.log('Generic token is not set. Generating a new one...'); }

        const appTokenRequest: AppTokenRequestConfig = {
            authority: identityServerConfig.authority,
            scope: TokenScopes.DFS_API,
            grant_type: TokenGrantTypes.GENERIC,
            client_id: identityServerConfig.client_id,
            tenant_uid: identityServerConfig.tenant_uid,
            response_type: identityServerConfig.response_type,
            acr_values: identityServerConfig.acr_values,
            access_token: setupData.member_token,
        };

        return new Promise((resolve, reject) => {
            Security.getAppToken(appTokenRequest).then((response) => {
                if (response.status === STATUS_OK) {
                    setup({
                        application_generic_token: `Bearer ${response.data.access_token}`,
                    });
                } else {
                    reject(response.data.apiError);
                }

                if (Security.showTokenLogs) { console.log('addGenericToken() -> ', `Bearer ${response.data.access_token}`); }

                Security.CacheTokenByType(TokenTypes.application_generic_token, `Bearer ${response.data.access_token}`);
                resolve(response.data);
            }).catch((e: AxiosError) => {
                reject(e);
            });
        });
    }

    static addAppToken() {
        const setupData = getSetupData();
        const identityServerConfig = getIdentityServerConfig();

        const getCachedToken = Security.GetCachedTokenByType(TokenTypes.application_token);

        if (getCachedToken && !Security.isTokenExpired(getCachedToken)) {
            setup({
                application_token: getCachedToken,
            });

            return new Promise((resolve, reject) => { resolve(getCachedToken); });
        }

        if (Security.showLogs) { console.log('App token is not set. Generating a new one...'); }

        const appTokenRequest: AppTokenRequestConfig = {
            authority: identityServerConfig.authority,
            scope: TokenScopes.DFS_API,
            grant_type: TokenGrantTypes.CLIENT_CREDENTIALS,
            client_id: identityServerConfig.client_id,
            tenant_uid: identityServerConfig.tenant_uid,
            response_type: identityServerConfig.response_type,
            acr_values: identityServerConfig.acr_values,
            access_token: setupData.member_token,
        };

        return new Promise((resolve, reject) => {
            Security.getAppToken(appTokenRequest).then((response) => {
                if (response.status === STATUS_OK) {
                    setup({
                        application_token: `Bearer ${response.data.access_token}`,
                    });
                } else {
                    reject(response.data.apiError);
                }

                if (Security.showTokenLogs) { console.log('addAppToken() -> ', `Bearer ${response.data.access_token}`); }

                Security.CacheTokenByType(TokenTypes.application_token, `Bearer ${response.data.access_token}`);
                resolve(response.data);
            }).catch((e: AxiosError) => {
                console.log(e);
                reject(e);
            });
        });
    }

    static async generateApplicationTokensIfNeeded(serviceType = ServiceTypes.DFS, isSecure = true) {
        if (Security.showLogs) { console.log(`generateApplicationTokensIfNeeded(serviceType = ${serviceType}, isSecure = ${isSecure})`); }

        return Security.addMemberToken()
            .then(() => Security.addGenericToken())
            .then(() => Security.addAppToken())
            .then(() => {
                const jwt = Security.GetCachedTokenByType(TokenTypes.member_token);

                if (jwt) {
                    return Security.generateNotificationHubAccessToken(jwt);
                }

                return Promise.resolve();
            })
            .then(() => {
                Security.setBearerAccessToken(serviceType, isSecure);

                return null;
            });
    }

    static setBearerAccessToken(serviceType: string, isSecure: boolean) {
        const setupData = getSetupData();

        if (Security.showSetTokensLogs) { console.log(`setBearerAccessToken(serviceType: ${serviceType}, isSecure: ${isSecure})`); }

        switch (serviceType) {
            case ServiceTypes.DFS:
                if (isSecure) {
                    setup({
                        bearerAccessToken: `Bearer ${setupData.member_token}`,
                    });
                } else {
                    setup({
                        bearerApplicationAccessToken: `Bearer ${setupData.member_token}`,
                    });
                }
                break;
            case ServiceTypes.DMS:
                if (isSecure) {
                    setup({
                        bearerAccessToken: `Bearer ${setupData.member_token}`,
                    });
                } else {
                    setup({
                        bearerApplicationAccessToken: `Bearer ${setupData.member_token}`,
                    });
                }
                break;
            case ServiceTypes.WITH_APP_TOKEN:
                setup({
                    bearerApplicationAccessToken: `${setupData.application_token}`,
                });
                break;
            case ServiceTypes.WITH_MEMBER_TOKEN:
                if (isSecure) {
                    setup({
                        bearerAccessToken: `Bearer ${setupData.member_token}`,
                    });
                } else {
                    setup({
                        bearerApplicationAccessToken: `Bearer ${setupData.member_token}`,
                    });
                }
                break;
            default:
                console.log(`ERROR: SERVICE TYPE ${serviceType} WITH SECURED ${isSecure} IS OUT OF BEARER ACCESS TOKEN SCOPE!!!`);
        }
    }
}

export default Security;
export const security = new Security();
